import React from 'react';
import { Link } from 'react-router-dom';
import { Menu, X, Sun, Moon, ArrowRight } from 'lucide-react';
import logoLight from '../../assets/brand/logo-light.png';
import logoDark from '../../assets/brand/logo-dark.png';

// Entries with a "#" are anchors on the homepage's own sections - always
// pathed as "/login#..." (not bare "#...") so they still resolve from any
// other page (e.g. /our-team): a plain <a> to a path+hash does a same-
// document instant scroll when already on /login (browsers treat identical-
// path, different-hash as in-page navigation) and a full navigation to
// /login followed by a native scroll-to-hash otherwise. A bare "#about"
// only ever worked on the homepage itself, which broke this exact click
// from /our-team (reported live, 2026-08-24). Hash-free entries (e.g.
// "/our-team") are real standalone routes and render via react-router's
// <Link> below instead, for a client-side transition with no reload.
const NAV_LINKS = [
  { href: '/login#about', label: 'About' },
  { href: '/login#features', label: 'Features' },
  { href: '/login#how-it-works', label: 'How It Works' },
  { href: '/login#pricing', label: 'Pricing' },
  { href: '/our-team', label: 'Our Team' },
];

export default function LandingHeader({
  isDarkTheme,
  onToggleTheme,
  mobileMenuOpen,
  onToggleMobileMenu,
  onLoginClick,
}) {
  const navBg = isDarkTheme ? 'bg-navy/70' : 'bg-white/80';
  const navBorder = isDarkTheme ? 'border-white/10' : 'border-gray-200';
  const linkColor = isDarkTheme
    ? 'text-white/80 hover:text-white'
    : 'text-text-dark hover:text-brand-blue';

  return (
    <header className={`relative z-20 border-b ${navBorder} backdrop-blur-xl sticky top-0`}>
      <nav className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between ${navBg}`}>
        {/* Logo: the light-mode PNG has a baked-in white background (no alpha
            channel - branding-resources logos are flat PNGs), so it's only
            placed here where the header bg is white/near-white; the dark-mode
            variant (baked-in navy bg) is used when the header itself is navy. */}
        <a href="/login#top" className="flex items-center shrink-0">
          <img
            src={isDarkTheme ? logoDark : logoLight}
            alt="RUDVAN - Adapt. Practice. Master."
            className="h-9 sm:h-12 w-auto"
          />
        </a>

        {/* Desktop Menu. Fixed 2026-09-02 (real tablet screenshot bug
            report): was gap-8 - CSS flex `gap` is a documented failure mode
            with browser/Google Translate widgets, which wrap translated text
            in <font> tags and can break gap's flex-item spacing calculation
            (the reported tablet screenshot showed an active EN/TA translate
            toolbar). space-x-8 applies margin-left to each <a>/<Link> itself
            via the `> * + *` sibling selector, so it's unaffected by
            whatever Translate does to the text *inside* each link -
            verified this container's spacing is otherwise correct (computed
            gap: 32px) across 4 real tablet widths (768/810/834/1024px) via a
            live Playwright repro; this class swap doesn't change that. */}
        <div className="hidden nav:flex items-center space-x-8">
          {NAV_LINKS.map((link) => (
            link.href.includes('#') ? (
              <a key={link.href} href={link.href} className={`${linkColor} transition-colors text-sm font-medium`}>
                {link.label}
              </a>
            ) : (
              <Link key={link.href} to={link.href} className={`${linkColor} transition-colors text-sm font-medium`}>
                {link.label}
              </Link>
            )
          ))}
        </div>

        {/* Right side: theme toggle + Login stay visible beside the
            hamburger on mobile now, instead of being hidden inside the
            dropdown menu (explicit user request, docs/DECISIONS.md,
            "Landing page mobile layout" decision) - only the About/
            Features/How It Works/Pricing nav links collapse into the
            hamburger below md:. Sizes/spacing shrink below sm: (icon-only
            theme toggle, tighter Login padding, no arrow icon) purely to
            fit the row on a phone width; at sm: and up every class here
            matches what this row always rendered, so desktop/tablet are
            unchanged.
            Fixed 2026-09-02 (real tablet screenshot, 768px width - exactly
            Tailwind's md breakpoint, also a real device size, classic iPad/
            iPad mini portrait): added shrink-0 - without it, this div had
            no flex-shrink protection, so when the 5-link nav (already
            wrapping "How It Works"/"Our Team" onto 2 lines to fit) squeezed
            the whole nav row at exactly this width, the browser shrank this
            block too, clipping the Login button off the right edge instead
            of the nav-links div (which already degrades gracefully via
            per-link text wrap) absorbing all of the squeeze. 810px+ never
            needed this - only 768px sat exactly on the edge. */}
        <div className="flex items-center gap-2 sm:gap-4 shrink-0">
          <button
            onClick={onToggleTheme}
            className={`p-2 rounded-btn ${isDarkTheme ? 'bg-white/10 hover:bg-white/20' : 'bg-surface-light hover:bg-gray-200'} transition-colors`}
            title={isDarkTheme ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
          >
            {isDarkTheme ? <Sun size={18} className="text-brand-cyan" /> : <Moon size={18} className="text-navy" />}
          </button>

          <button
            onClick={onLoginClick}
            className="flex items-center gap-1.5 sm:gap-2 px-3 sm:px-6 py-2 rounded-btn bg-brand-blue hover:bg-brand-blue-hover text-white text-sm sm:text-base font-semibold transition-all duration-300 shadow-brand-btn"
          >
            Login
            <ArrowRight size={18} className="hidden sm:inline" />
          </button>

          {/* Mobile Menu Button - now only toggles the nav-links dropdown
              below, since theme/Login live in this same row already.
              Kept in sync with the desktop nav's `nav:` breakpoint (2026-09-02
              fix) - must switch at the same width, or 768-799px would hide
              both the desktop nav AND this button, leaving no way to reach
              the nav links at all. */}
          <button
            onClick={onToggleMobileMenu}
            className={`nav:hidden ${isDarkTheme ? 'text-white' : 'text-navy'}`}
            aria-label="Toggle navigation menu"
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </nav>

      {mobileMenuOpen && (
        <div className={`nav:hidden border-t ${navBorder} ${navBg} backdrop-blur-xl`}>
          <div className="px-4 py-4 space-y-3">
            {NAV_LINKS.map((link) => (
              link.href.includes('#') ? (
                <a key={link.href} href={link.href} className={`block ${linkColor} text-sm font-medium`}>
                  {link.label}
                </a>
              ) : (
                <Link key={link.href} to={link.href} className={`block ${linkColor} text-sm font-medium`}>
                  {link.label}
                </Link>
              )
            ))}
          </div>
        </div>
      )}
    </header>
  );
}
